import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { History, Download, Briefcase, CheckCircle, Calendar, Search, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getFilingHistory } from '@/lib/dataService';
import { getStatusBadge } from '@/lib/statusConfig';
import { formatDate } from '@/lib/formatters';
import { jobsApi } from '@/services/api';
import { toast } from 'sonner';

const FilingHistory = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    getFilingHistory()
      .then(setJobs)
      .catch(err => {
        console.error('Failed to load filing history:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  // Derive available years from job data
  const availableYears = useMemo(() => {
    const years = [...new Set(jobs.map(j => j.financialYear).filter(Boolean))].sort((a, b) => b.localeCompare(a));
    return years;
  }, [jobs]);

  const filtered = useMemo(() => {
    return jobs.filter(job => {
      const matchesYear = yearFilter === 'all' || job.financialYear === yearFilter;
      const matchesSearch =
        !search ||
        job.companyName?.toLowerCase().includes(search.toLowerCase()) ||
        job.jobId?.toLowerCase().includes(search.toLowerCase());
      return matchesYear && matchesSearch;
    });
  }, [jobs, yearFilter, search]);

  const filedThisYear = useMemo(() => {
    const year = new Date().getFullYear().toString();
    return jobs.filter(j => j.financialYear?.includes(year)).length;
  }, [jobs]);

  const handleDownload = async (job) => {
    if (job.tagfshStatus !== 'packaged') {
      toast.info('Download will be available when conversion is complete.');
      return;
    }
    try {
      const { blob, filename } = await jobsApi.downloadIxbrlPackage(job.id || job.jobId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || 'Could not download the iXBRL package.');
    }
  };

  const FilingStatusPill = ({ label }) => {
    if (!label) return <span className="text-xs text-muted-foreground">—</span>;
    const clean = label.replace(/_/g, ' ');
    const isAccepted = label.includes('accept') || label.includes('success') || label.includes('filed');
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isAccepted ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
        {clean}
      </span>
    );
  };

  const ServiceBadge = ({ service }) => {
    const labels = {
      financialStatements: 'iXBRL',
      taxComputation: 'Tax',
      ct600: 'CT600',
      companiesHouse: 'CH',
    };
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
        {labels[service] || service}
      </span>
    );
  };

  const SkeletonRow = () => (
    <div className="flex items-center gap-4 p-4 border border-border rounded-lg">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-4 w-16 ml-auto" />
    </div>
  );

  return (
    <div data-testid="filing-history-page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Filing History</h1>
          <p className="page-subtitle">
            {loading ? 'Loading…' : `${jobs.length} completed ${jobs.length === 1 ? 'filing' : 'filings'}`}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/jobs')}>
          Active Jobs
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* KPI row */}
      <div className="stat-grid mb-6">
        <div className="stat-card bg-card border border-border rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <History className="w-5 h-5 text-teal-700" />
            </div>
          </div>
          <p className="text-2xl font-semibold mb-1">{loading ? <Skeleton className="h-7 w-10" /> : jobs.length}</p>
          <p className="text-sm text-muted-foreground">Total Filed</p>
        </div>

        <div className="stat-card bg-card border border-border rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-700" />
            </div>
          </div>
          <p className="text-2xl font-semibold mb-1">{loading ? <Skeleton className="h-7 w-10" /> : filedThisYear}</p>
          <p className="text-sm text-muted-foreground">Completed This Year</p>
        </div>

        <div className="stat-card bg-card border border-border rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-blue-700" />
            </div>
          </div>
          <p className="text-2xl font-semibold mb-1">{loading ? <Skeleton className="h-7 w-10" /> : availableYears.length}</p>
          <p className="text-sm text-muted-foreground">Financial Years</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by company or job ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
          <select
            value={yearFilter}
            onChange={e => setYearFilter(e.target.value)}
            className="text-sm border border-border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="all">All years</option>
            {availableYears.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Job list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <SkeletonRow key={i} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-lg" data-testid="empty-history">
          <History className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-base font-semibold mb-1">
            {jobs.length === 0 ? 'No filed jobs yet' : 'No results'}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {jobs.length === 0
              ? 'Your completed filings will appear here once jobs are filed.'
              : 'Try adjusting your search or year filter.'}
          </p>
          {jobs.length === 0 && (
            <Button size="sm" onClick={() => navigate('/wizard/start')}>
              Start a New Filing
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {/* Table header — hidden on mobile */}
          <div className="hidden md:grid grid-cols-[1fr_120px_180px_140px_140px_120px] gap-4 px-4 py-3 border-b border-border bg-muted/30 text-xs font-medium text-muted-foreground uppercase tracking-wide">
            <span>Company</span>
            <span>Year</span>
            <span>Services</span>
            <span>CH Filing</span>
            <span>HMRC Filing</span>
            <span className="text-right">Action</span>
          </div>

          <div className="divide-y divide-border">
            {filtered.map(job => (
              <div
                key={job.id}
                className="grid grid-cols-1 md:grid-cols-[1fr_120px_180px_140px_140px_120px] gap-2 md:gap-4 px-4 py-4 hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/jobs/${job.jobId}`)}
              >
                {/* Company + Job ID */}
                <div>
                  <p className="font-medium text-sm">{job.companyName}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {job.jobId} &middot; {formatDate(job.date)}
                  </p>
                  {/* Status badge on mobile only */}
                  <div className="mt-1 md:hidden">
                    {getStatusBadge(job.status)}
                  </div>
                </div>

                {/* Financial year */}
                <div className="flex items-center">
                  <span className="text-sm">{job.financialYear || '—'}</span>
                </div>

                {/* Services */}
                <div className="flex flex-wrap items-center gap-1">
                  {(job.services || []).map(svc => (
                    <ServiceBadge key={svc} service={svc} />
                  ))}
                  {!job.services?.length && <span className="text-xs text-muted-foreground">—</span>}
                </div>

                {/* CH status */}
                <div className="flex items-center">
                  <FilingStatusPill label={job.chFilingStatus} />
                </div>

                {/* HMRC status */}
                <div className="flex items-center">
                  <FilingStatusPill label={job.hmrcFilingStatus} />
                </div>

                {/* Download */}
                <div className="flex items-center justify-end" onClick={e => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(job)}
                    className="text-xs"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilingHistory;
